import React from "react";
import { useSelector } from "react-redux";
import cx from 'classnames';
import {
  PAGE, BASIC_TEXT, TEXT, IMAGE, VIDEO, BUTTON, SHAPE, LINE, RECTANGLE,
  IFRAME, HTML, ANCHOR,
  FORM, FORM_STEP, FORM_FIELD, FORM_SUBMIT,
  SECTION, SECTION_COLUMN,
  LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER,
  CONTENT, CONTENT_CARD, CONTENT_CARD_TEMPLATE, CONTENT_PAGINATION_BUTTON, ASSET,
  GROUP, MODAL, EXTERNAL_FORM, MENU_CONTAINER, MENU, HOTSPOT, CONTENT_FILTER,
  CONTENT_SEARCH, CONTENT_FILTER_CONTAINER, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING_MESSAGE,
  CAROUSEL, CAROUSEL_CARD, SEEK, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX,
  FORM_NUMBER, FORM_TEXTAREA,
  OTP_FORM_SUBMIT, OTP_RESEND, OTP_INPUT, SLIDE,
  MENU_DRAGGABLE_CONTAINER,
  CHART, CONTAINER,
} from '../../../constants/widgetTypes';

import { WidgetsContext } from "./context";
import { getStylesForLayout, getChildWidgetProps } from "../widget.helper";
import { FlexFlowContext, RESET_FLEX_CONTEXT, flowsChildrenInFlex, allColumnsInFlexMode, CONTENT_DRIVEN_SECTION_HEIGHT, toCssDimension, flexValueToCss } from "../flex/flexContext";
import { getFlexFlowConfig } from "../flex/flexFlow";
import { useWidgetChildrenSelector, useContainerSelector } from "./hooks";
import { LazyLoadWhenVisible } from "./LazyLoadWhenVisible";
import { resetMarginOnTablet, useInteractiveContent } from "../InteractiveContent/v2";

import { PageWidget } from '../page';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import set from 'lodash/set';
import { dynamicImport } from "../../../services/dynamicImport";

import styles from '../widget.module.scss';


const BasicTextWidget = React.lazy(() =>
  import('../basicText').then((module) => ({ default: module.BasicTextWidget }))
)
const TextWidget = React.lazy(() =>
  import('../text').then((module) => ({ default: module.TextWidget }))
)
const ImageWidget = React.lazy(() =>
  import('../image').then((module) => ({ default: module.ImageWidget }))
)
const VideoWidget = React.lazy(() =>
  import('../video').then((module) => ({ default: module.VideoWidget }))
)
const ButtonWidget = React.lazy(() =>
  import('../button').then((module) => ({ default: module.ButtonWidget }))
)
const ShapeWidget = React.lazy(() =>
  import('../shape').then((module) => ({ default: module.ShapeWidget }))
)
const LineWidget = React.lazy(() =>
  import('../line').then((module) => ({ default: module.LineWidget }))
)
const RectangleWidget = React.lazy(() =>
  import('../rectangle').then((module) => ({ default: module.RectangleWidget }))
)
const IFrameWidget = React.lazy(() =>
  import('../iframe').then((module) => ({ default: module.IFrameWidget }))
)
const ChartWidget = React.lazy(() =>
  import('../chart').then((module) => ({ default: module.ChartWidget }))
)
const HtmlWidget = React.lazy(() =>
  import('../html').then((module) => ({ default: module.HtmlWidget }))
)
const ExternalFormWidget = React.lazy(() =>
  import('../externalForm').then((module) => ({
    default: module.ExternalFormWidget
  }))
)
const FormWidget = React.lazy(() =>
  import('../form').then((module) => ({ default: module.FormWidget }))
)
const FormStepWidget = React.lazy(() =>
  import('../form/steps').then((module) => ({ default: module.FormStepWidget }))
)
const FormTextWidget = React.lazy(() =>
  import('../form/Fields/textField').then((module) => ({
    default: module.FormTextWidget
  }))
)
const FormOtpInputWidget = React.lazy(() =>
  import('../form/Fields/otpInput').then((module) => ({
    default: module.FormOtpInputWidget
  }))
)
const FormDropdownList = React.lazy(() =>
  import('../form/Fields/dropdownList').then((module) => ({
    default: module.FormDropdownList
  }))
)
const FormCheckBoxListWidget = React.lazy(() =>
  import('../form/Fields/checkBoxList').then((module) => ({
    default: module.FomrCheckBoxListWidget
  }))
)
const FormCheckBoxField = React.lazy(() =>
  import('../form/Fields/checkBox').then((module) => ({
    default: module.FormCheckBoxField
  }))
)
const FormNumberWidget = React.lazy(() =>
  import('../form/Fields/numberField').then((module) => ({
    default: module.FormNumberWidget
  }))
)
const FormTextAreaWidget = React.lazy(() =>
  import('../form/Fields/textArea').then((module) => ({
    default: module.FormTextAreaWidget
  }))
)
const SubmitButtonWidget = React.lazy(() =>
  import('../form/submit').then((module) => ({
    default: module.SubmitButtonWidget
  }))
)
const OtpSubmitButtonWidget = React.lazy(() =>
  import('../form/otpSubmit').then((module) => ({
    default: module.OtpSubmitButtonWidget
  }))
)
const OtpResendButtonWidget = React.lazy(() =>
  import('../form/otpResend').then((module) => ({
    default: module.OtpResendButtonWidget
  }))
)
const AnchorWidget = React.lazy(() =>
  import('../anchor').then((module) => ({ default: module.AnchorWidget }))
)
const LiveElementWidget = React.lazy(() =>
  import('../liveElements').then((module) => ({
    default: module.LiveElementWidget
  }))
)
const LiveElementContainerWidget = React.lazy(() =>
  import('../liveElements/container').then((module) => ({
    default: module.LiveElementContainerWidget
  }))
)
import { SectionWidget } from "../section";
import { SectionColumnWidget } from "../section/column";

const ContentWidget = React.lazy(() =>
  import('../content').then((module) => ({ default: module.ContentWidget }))
)
const ContentCardWidget = React.lazy(() =>
  import('../content/card').then((module) => ({
    default: module.ContentCardWidget
  }))
)
const PlaceholderCardWidget = React.lazy(() =>
  import('../content/placeholder').then((module) => ({
    default: module.PlaceholderCardWidget
  }))
)
const FilterWidget = React.lazy(() =>
  import('../content/filters').then((module) => ({
    default: module.FilterWidget
  }))
)
const FilterContainerWidget = React.lazy(() =>
  import('../content/filters/filterContainer').then((module) => ({
    default: module.FilterContainerWidget
  }))
)
const SearchContainerWidget = React.lazy(() =>
  import('../content/filters/searchContainer').then((module) => ({
    default: module.SearchContainerWidget
  }))
)
const PaginationButtonWidget = React.lazy(() =>
  import('../content/pagination').then((module) => ({
    default: module.PaginationButtonWidget
  }))
)
const EmptyMessageWidget = React.lazy(() =>
  import('../content/emptyMessage').then((module) => ({
    default: module.EmptyMessageWidget
  }))
)
const LoadingWidget = React.lazy(() =>
  import('../content/loading').then((module) => ({
    default: module.LoadingWidget
  }))
)
const GroupWidget = React.lazy(() =>
  import('../group').then((module) => ({ default: module.GroupWidget }))
)
const ModalWidget = React.lazy(() =>
  import('../modal').then((module) => ({ default: module.ModalWidget }))
)
const MenuWidget = React.lazy(() =>
  import('../menu').then((module) => ({ default: module.MenuWidget }))
)
const MenuContainerWidget = React.lazy(() =>
  import('../menu/menuContainer').then((module) => ({
    default: module.MenuContainerWidget
  }))
)
const CarouselWidget = React.lazy(() =>
  import('../carousel').then((module) => ({ default: module.CarouselWidget }))
)
const CarouselCardWidget = React.lazy(() =>
  import('../carousel/card').then((module) => ({
    default: module.CarouselCardWidget
  }))
)
const HotspotWidget = React.lazy(() =>
  import('../hotspot').then((module) => ({ default: module.HotspotWidget }))
)
const AssetWidget = React.lazy(() =>
  import('./../asset').then((module) => ({ default: module.AssetWidget }))
)
const ContainerWidget = React.lazy(() =>
  import('../container').then((module) => ({ default: module.ContainerWidget }))
)
const SeekWidget = React.lazy(() =>
  import('../seek').then((module) => ({ default: module.SeekWidget }))
)
const SlideWidget = React.lazy(() =>
  import('../slide').then((module) => ({ default: module.SlideWidget }))
)

const getWidgetComponentFotType = (type) => {

  switch (type) {
    case PAGE:
      return PageWidget;
    case BASIC_TEXT:
      return BasicTextWidget;
    case TEXT:
      return TextWidget;
    case IMAGE:
      return ImageWidget;
    case VIDEO:
      return VideoWidget;
    case BUTTON:
      return ButtonWidget;
    case SHAPE:
      return ShapeWidget;
    case LINE:
      return LineWidget;
    case RECTANGLE:
      return RectangleWidget;
    case IFRAME:
      return IFrameWidget;
    case CHART:
      return ChartWidget;
    case HTML:
      return HtmlWidget;
    case EXTERNAL_FORM:
      return ExternalFormWidget;
    case FORM:
      return FormWidget;
    case FORM_STEP:
      return FormStepWidget;
    case FORM_FIELD:
      return FormTextWidget;
    case OTP_INPUT:
      return FormOtpInputWidget;
    case FORM_DROPDOWN_LIST:
      return FormDropdownList;
    case FORM_CHECK_LIST:
      return FormCheckBoxListWidget;
    case FORM_CHECK_BOX:
      return FormCheckBoxField;
    case FORM_NUMBER:
      return FormNumberWidget;
    case FORM_TEXTAREA:
      return FormTextAreaWidget;
    case FORM_SUBMIT:
      return SubmitButtonWidget;
    case OTP_FORM_SUBMIT:
      return OtpSubmitButtonWidget;
    case OTP_RESEND:
      return OtpResendButtonWidget;
    case ANCHOR:
      return AnchorWidget;
    case LIVE_ELEMENT:
      return LiveElementWidget;
    case LIVE_ELEMENT_CONTAINER:
      return LiveElementContainerWidget;
    case SECTION:
      return SectionWidget;
    case SECTION_COLUMN:
      return SectionColumnWidget;
    case CONTAINER:
      return ContainerWidget;
    case CONTENT:
      return ContentWidget;
    case CONTENT_CARD:
      return ContentCardWidget;
    case CONTENT_CARD_TEMPLATE:
      return PlaceholderCardWidget;
    case CONTENT_FILTER:
      return FilterWidget;
    case CONTENT_FILTER_CONTAINER:
      return FilterContainerWidget;
    case CONTENT_SEARCH:
      return SearchContainerWidget;
    case CONTENT_PAGINATION_BUTTON:
      return PaginationButtonWidget;
    case CONTENT_EMPTY_MESSAGE:
      return EmptyMessageWidget;
    case CONTENT_LOADING_MESSAGE:
      return LoadingWidget;
    case GROUP:
      return GroupWidget;
    case MODAL:
      return ModalWidget;
    case MENU:
      return MenuWidget;
    case MENU_CONTAINER:
    case MENU_DRAGGABLE_CONTAINER:
      return MenuContainerWidget;
    case CAROUSEL:
      return CarouselWidget;
    case CAROUSEL_CARD:
      return CarouselCardWidget;
    case HOTSPOT:
      return HotspotWidget;
    case ASSET:
      return AssetWidget;
    case SEEK:
      return SeekWidget;
    case SLIDE:
      return SlideWidget;
    default:
      return false
  }
}

const FORM_FIELD_TYPES = [
  FORM_FIELD,
  FORM_TEXTAREA,
  FORM_DROPDOWN_LIST,
  FORM_CHECK_LIST,
  FORM_NUMBER,
  FORM_CHECK_BOX
];

export const filterAndRepositionForProgressiveGating = (widgets, clusterWidgetIds, breakpoint, formWidget, formStepWidget) => {
  if (!widgets || !clusterWidgetIds || clusterWidgetIds.length === 0) {
    return {
      widgets: widgets || [],
      updatedFormWidget: null
    };
  }

  const formWidth = get(formWidget, `properties.${breakpoint}.layout.width`, '600px');
  const formWidthValue = parseFloat(formWidth);

  const widgetsWithContentId = [];
  const widgetsWithoutContentId = [];
  const submitButton = [];

  widgets.forEach(widget => {
    const contentId = get(widget, 'content.id', null);
    const widgetType = get(widget, 'type', '');
    const translate = get(widget, `properties.${breakpoint}.layout.transform.translate`, '0px,0px');
    const originalY = parseFloat(translate.split(',')[1]);
    
    if (widgetType === FORM_SUBMIT) {
      submitButton.push({ widget, originalY });
    } else if (contentId && FORM_FIELD_TYPES.includes(widgetType)) {
      widgetsWithContentId.push({ widget, originalY });
    } else {
      widgetsWithoutContentId.push({ widget, originalY });
    }
  });

  let filteredWidgets = widgetsWithContentId.filter(item => 
    clusterWidgetIds.includes(item.widget.content.id)
  );

  if (filteredWidgets.length === 0) {
    return {
      widgets: [...widgetsWithoutContentId.map(i => i.widget), ...submitButton.map(i => i.widget)],
      updatedFormWidget: null
    };
  }

  filteredWidgets = filteredWidgets.sort((a, b) => a.originalY - b.originalY);

  const firstFieldY = filteredWidgets[0].originalY;
  const lastFieldY = filteredWidgets[filteredWidgets.length - 1].originalY;

  const staticWidgetsAbove = widgetsWithoutContentId.filter(item => item.originalY < firstFieldY);
  const staticWidgetsBetweenOrBelow = widgetsWithoutContentId.filter(item => item.originalY >= firstFieldY);

  const fieldSpacing = 20;
  const startPadding = 30;
  
  let currentY = startPadding;
  if (staticWidgetsAbove.length > 0) {
    const lastAboveWidget = staticWidgetsAbove[staticWidgetsAbove.length - 1];
    const height = get(lastAboveWidget.widget, `properties.${breakpoint}.layout.height`, 'auto');
    const widgetHeight = height === 'auto' ? 50 : parseFloat(height);
    currentY = lastAboveWidget.originalY + widgetHeight + fieldSpacing;
  }

  const repositionedWidgets = filteredWidgets.map((item, index) => {
    const clonedWidget = cloneDeep(item.widget);
    const translate = get(item.widget, `properties.${breakpoint}.layout.transform.translate`, '0px,0px');
    const [x, y] = translate.split(',');
    const height = get(item.widget, `properties.${breakpoint}.layout.height`, 'auto');
    
    const fieldHeight = height === 'auto' ? 60 : parseFloat(height);
    
    set(
      clonedWidget, 
      `properties.${breakpoint}.layout.transform.translate`, 
      `${startPadding}px,${currentY}px`
    );
    
    const newWidth = formWidthValue - (startPadding * 2);
    set(
      clonedWidget,
      `properties.${breakpoint}.layout.width`,
      `${newWidth}px`
    );
    
    currentY += fieldHeight + fieldSpacing;
    
    return clonedWidget;
  });

  const repositionedStaticWidgets = staticWidgetsBetweenOrBelow.map(item => {
    const clonedWidget = cloneDeep(item.widget);
    const height = get(item.widget, `properties.${breakpoint}.layout.height`, 'auto');
    const widgetHeight = height === 'auto' ? 50 : parseFloat(height);
    
    const translate = get(item.widget, `properties.${breakpoint}.layout.transform.translate`, '0px,0px');
    const [x, y] = translate.split(',');
    
    set(
      clonedWidget,
      `properties.${breakpoint}.layout.transform.translate`,
      `${x},${currentY}px`
    );
    
    currentY += widgetHeight + fieldSpacing;
    
    return clonedWidget;
  });

  const repositionedSubmitButton = submitButton.map(item => {
    const clonedWidget = cloneDeep(item.widget);
    const height = get(item.widget, `properties.${breakpoint}.layout.height`, '40px');
    const buttonHeight = parseFloat(height);
    
    const extraTopSpacing = 30;
    
    set(
      clonedWidget,
      `properties.${breakpoint}.layout.transform.translate`,
      `${startPadding}px,${currentY + extraTopSpacing}px`
    );
    
    currentY += buttonHeight + startPadding + extraTopSpacing;
    
    return clonedWidget;
  });
  
  const newFormHeight = currentY;
  const updatedFormWidget = cloneDeep(formWidget);
  set(
    updatedFormWidget,
    `properties.${breakpoint}.layout.height`,
    `${newFormHeight}px`
  );

  const result = [
    ...staticWidgetsAbove.map(i => i.widget),
    ...repositionedWidgets,
    ...repositionedStaticWidgets,
    ...repositionedSubmitButton
  ];

  return {
    widgets: result,
    updatedFormWidget: updatedFormWidget,
    newFormHeight: newFormHeight
  };
};

const WidgetComponent = ({ widgetProps, breakpoint, ...rest }) => {
  const widgetContext = React.useContext(WidgetsContext);
  const id = get(widgetProps, 'uid');
  const container = useContainerSelector(id);
  const widgetChildren = useWidgetChildrenSelector(id);
  const parentContainer = useContainerSelector(container?.uid);
  const nextjsRendering = get(rest, 'nextjsRendering', null);
  const { tabletScreens = false } = useInteractiveContent()
  const lastCalculatedModalHeightRef = React.useRef(null);
  const lastCalculatedFormHeightRef = React.useRef(null);

  const context = React.useContext(WidgetsContext);
  const selector = context.selector;
  const allWidgets = useSelector(state => get(state, `${selector}.widgets`) || []);
  const updateWidgetHeight = get(context, 'updateWidgetHeight');
  
  const progressiveGating = get(context, 'progressiveGating', {});
  const isProgressiveEnabled = progressiveGating.enabled || false;
  const clusterFields = progressiveGating.clusterFields || [];

  const type = get(widgetProps, 'type');
  const Component = getWidgetComponentFotType(type);

  const { flexMode: inFlexFlow, fitItems, flexLayout: parentFlexLayout } = React.useContext(FlexFlowContext);
  const flowsOwnChildren = flowsChildrenInFlex(type, widgetProps);
  const sectionAllColumnsFlex = type === SECTION && allColumnsInFlexMode(widgetChildren);
  const containerInFlexMode = type === CONTAINER && flowsOwnChildren;
  const containerFluidNeedsHeight = type === CONTAINER && !containerInFlexMode;

  const layout = get(widgetProps, `properties.${breakpoint}.layout`);
  var widgetStyle = getStylesForLayout(layout, breakpoint, container, parentContainer, widgetContext);
  widgetStyle = {
    ...widgetStyle,
    ...resetMarginOnTablet(tabletScreens)
  }

  if (inFlexFlow) {
    const flexItemWidth = toCssDimension(widgetStyle.width);
    const flexItemHeight = toCssDimension(widgetStyle.height);
    const { preserveSize, preserveWidth } = getFlexFlowConfig(type);
    const keepHeight = preserveSize || containerFluidNeedsHeight;
    const dropProps = ['position', 'left', 'top', 'transform', 'marginLeft'];

    if (!keepHeight) {
      dropProps.push('height');
    }

    if (!preserveSize && !preserveWidth) {
      dropProps.push('width');
    }

    widgetStyle = omit(widgetStyle, dropProps);
    widgetStyle.position = 'relative';

    if (fitItems) {
      if (flexItemWidth) {
        widgetStyle.width = flexItemWidth;
        widgetStyle.maxWidth = '100%';
        widgetStyle.flexShrink = 0;
      }
      if (flexItemHeight && (preserveSize || keepHeight)) {
        widgetStyle.height = flexItemHeight;
      }
    }

    if (!fitItems && preserveWidth && flexItemWidth && flexValueToCss(get(parentFlexLayout, 'flexWrap')) !== 'nowrap') {
      widgetStyle.maxWidth = '100%';
    }

    const parentDirection = get(parentFlexLayout, 'flexDirection');
    const isColumnAxis = parentDirection === 'column' || parentDirection === 'columnReverse';
    if (!fitItems && preserveSize && flexItemWidth && isColumnAxis) {
      widgetStyle.maxWidth = '100%';
    }

    const parentNoWrap = flexValueToCss(get(parentFlexLayout, 'flexWrap')) === 'nowrap';

    if (parentNoWrap) {
      widgetStyle.flexShrink = 0;
    }

    if (containerInFlexMode) {
      delete widgetStyle.height;
    }

    if (sectionAllColumnsFlex) {
      widgetStyle.height = CONTENT_DRIVEN_SECTION_HEIGHT;
    }

    if (type === SECTION && !sectionAllColumnsFlex && flexItemHeight) {
      widgetStyle.height = flexItemHeight;
    }
  }
  
  let visibleChildren = isProgressiveEnabled 
    ? widgetChildren 
    : widgetChildren?.filter(item =>
        get(item, `properties.${breakpoint}.visible`, true)
      );
  
  const clusterWidgetIds = isProgressiveEnabled ? clusterFields : [];
  const shouldApplyProgressive = isProgressiveEnabled && clusterWidgetIds.length > 0;
  
  const getChildrenOfWidget = (parentUid) => {
    const parentWidget = allWidgets.find(widget => widget.uid === parentUid);
    
    const isProgressiveEnabledForParent = 
      isProgressiveEnabled && 
      get(parentWidget, 'type') === FORM_STEP;
    
    const children = allWidgets
      .filter(item => item.parent === parentUid)
      .sort((w1, w2) => {
        const o1 = get(w1, 'order') || 1;
        const o2 = get(w2, 'order') || 1;
        return o1 - o2;
      });
    
    return isProgressiveEnabledForParent 
      ? children
      : children.filter(item => get(item, `properties.${breakpoint}.visible`, true));
  };
  
  const formHeightUpdatesRef = React.useRef([]);
  
  if ((type === MODAL || type === SLIDE) && shouldApplyProgressive) {
    formHeightUpdatesRef.current = [];
    
    visibleChildren = visibleChildren.map(child => {
      const childType = get(child, 'type');
  
      if (childType === FORM) {
        const formChildren = getChildrenOfWidget(child.uid);
        const formStepWidget = formChildren?.find(item => 
          get(item, 'type') === FORM_STEP
        );
        
        if (formStepWidget) {
          const formStepChildren = getChildrenOfWidget(formStepWidget.uid);
          
          const result = filterAndRepositionForProgressiveGating(
            formStepChildren,
            clusterWidgetIds,
            breakpoint,
            child,
            formStepWidget
          );
          
          if (result.updatedFormWidget && result.newFormHeight) {
            formHeightUpdatesRef.current.push({
              formUid: child.uid,
              newHeight: result.newFormHeight
            });
            
            const newHeight = get(result.updatedFormWidget, `properties.${breakpoint}.layout.height`);
            const clonedChild = cloneDeep(child);
            set(clonedChild, `properties.${breakpoint}.layout.height`, newHeight);
            return clonedChild;
          }
        }
      }
      
      return child;
    });
  }
  
  React.useEffect(() => {
    if ((type !== MODAL && type !== SLIDE) || !shouldApplyProgressive || !updateWidgetHeight) {
      return;
    }
    
    if (formHeightUpdatesRef.current.length > 0) {
      formHeightUpdatesRef.current.forEach(({ formUid, newHeight }) => {
        const heightKey = `${formUid}-${newHeight}`;
        
        if (lastCalculatedFormHeightRef.current !== heightKey) {
          lastCalculatedFormHeightRef.current = heightKey;
          updateWidgetHeight(context?.session, formUid, breakpoint, `${newHeight}px`);
        }
      });
    }
  }, [type, shouldApplyProgressive, updateWidgetHeight, context?.session, breakpoint]);
  
  React.useEffect(() => {
    if (type !== MODAL || !shouldApplyProgressive || visibleChildren.length === 0 || !updateWidgetHeight) {
      return;
    }

    const hasSuccessfulForm = visibleChildren.some(child => {
      const childType = get(child, 'type');
      if (childType === FORM) {
        return get(child, 'success', false) === true;
      }
      return false;
    });

    if (hasSuccessfulForm) {
      return;
    }
    
    const childrenWithPositions = visibleChildren.map(child => {
      const translate = get(child, `properties.${breakpoint}.layout.transform.translate`, '0px,0px');
      const y = parseFloat(translate.split(',')[1]);
      
      const childType = get(child, 'type');
      let childHeight = 0;
      
      if (childType === FORM) {
        const formHeight = get(child, `properties.${breakpoint}.layout.height`, 'auto');
        childHeight = formHeight === 'auto' ? 60 : parseFloat(formHeight);
      } else {
        const height = get(child, `properties.${breakpoint}.layout.height`, 'auto');
        childHeight = height === 'auto' ? 60 : parseFloat(height);
      }
      
      const bottom = y + childHeight;
      return { bottom };
    });
    
    if (childrenWithPositions.length === 0) return;
    
    const maxBottom = Math.max(...childrenWithPositions.map(c => c.bottom));
    
    const bottomPadding = 50;
    const newModalHeight = Math.max(maxBottom + bottomPadding, 500);
    
    const currentModalHeight = get(widgetProps, `properties.${breakpoint}.layout.height`, 'auto');
    const currentHeightValue = currentModalHeight === 'auto' ? 0 : parseFloat(currentModalHeight);
    
    if (lastCalculatedModalHeightRef.current !== newModalHeight || currentHeightValue !== newModalHeight) {
      lastCalculatedModalHeightRef.current = newModalHeight;
      updateWidgetHeight(context?.session, id, breakpoint, `${newModalHeight}px`);
    }
    
  }, [type, shouldApplyProgressive, visibleChildren, breakpoint, id, updateWidgetHeight, context?.session, widgetProps]);

  if (!Component) {
    console.warn(`Component not found for type ${type}`);
    return null;
  }
  
  if (type === FORM_STEP && shouldApplyProgressive) {
    const result = filterAndRepositionForProgressiveGating(
      visibleChildren,
      clusterWidgetIds, 
      breakpoint,
      container,
      widgetProps
    );
    
    visibleChildren = result.widgets || visibleChildren;
  }

  var children = visibleChildren?.map(item => {
    const childProps = getChildWidgetProps(item, widgetProps.type);
    return (
      <Widgets
        key={childProps.uid}
        widgetProps={childProps}
        breakpoint={breakpoint}
        nextjsRendering={nextjsRendering}
      />
    )
  });

  const isFirstFlowedSibling = React.useMemo(() => {
    if (type !== SECTION) return false;
    const parentId = get(widgetProps, 'parent');
    const siblings = allWidgets.filter(w => w.parent === parentId);
    if (siblings.length === 0) return true;
    const minOrder = siblings.reduce(
      (min, w) => ((get(w, 'order', 0) < get(min, 'order', 0)) ? w : min),
      siblings[0]
    );
    return get(minOrder, 'uid') === id;
  }, [type, allWidgets, widgetProps, id]);

  let element = React.createElement(Component, {
    widgetProps: widgetProps,
    className: cx(styles.widget, 'widget'),
    key: id,
    breakpoint: breakpoint,
    style: widgetStyle,
    nextjsRendering,
    inFlexFlow,
    parentFlexLayout,
    isFirstFlowedSibling,
    ...rest
  }, children);

  if (inFlexFlow && !flowsOwnChildren) {
    element = (
      <FlexFlowContext.Provider value={RESET_FLEX_CONTEXT}>
        {element}
      </FlexFlowContext.Provider>
    );
  }

  return (
    <React.Suspense fallback={null}>
      {element}
    </React.Suspense>
  );
}

const widgetEquals = function (prevProps, nextProps) {
  const paths = ['widgetProps', 'breakpoint'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const Widgets = React.memo(WidgetComponent, widgetEquals);